import { createContext, useContextSelector } from 'use-context-selector';
import { CatalogContext } from '@context/catalog.context';
import { ElasticSearch } from '@interfaces/models/elasticSearch';
import React, { useMemo, useState } from 'react';
import { removeKeys } from '@helpers/utils/general';
import { CatalogContextProps } from '@interfaces/models/catalogContextProps';
import { getApplyFilterPayload } from '@helpers/utils/catalog/catalog-tracking-utils';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { FilterSource } from '@enums/filter-source';
import { FilterSources } from '@interfaces/models/filter-sources';
import { SourceAddition, syncFilterSources } from '@helpers/utils/catalog/filter-sources-helpers';

const ORDERED_MATERIAL_MENU_LEVELS = ['materialLvl0', 'materialLvl1'] as const;

export type MaterialMenuLevels = (typeof ORDERED_MATERIAL_MENU_LEVELS)[number];

export type ParentMaterialMenuLevels = Exclude<MaterialMenuLevels, 'materialLvl1'>;

const getParentLevel = (level: MaterialMenuLevels): ParentMaterialMenuLevels | undefined => {
  const index = ORDERED_MATERIAL_MENU_LEVELS.indexOf(level);
  if (index === 0) {
    return;
  }
  return ORDERED_MATERIAL_MENU_LEVELS[index - 1] as ParentMaterialMenuLevels;
};

const getChildLevel = (level: MaterialMenuLevels): MaterialMenuLevels | undefined => {
  const index = ORDERED_MATERIAL_MENU_LEVELS.indexOf(level);
  if (index === ORDERED_MATERIAL_MENU_LEVELS.length - 1) {
    return;
  }
  return ORDERED_MATERIAL_MENU_LEVELS[index + 1];
};

type FacetConfig = {
  id: string;
  name: string;
} | null;

export interface MaterialMenuState {
  activeLevel: MaterialMenuLevels;
  firstLevel: MaterialMenuLevels;
  materialLvl0: FacetConfig;
  materialLvl1: FacetConfig;
}

interface CatalogMaterialHotfilterContextProps {
  applyMaterialHotFilter: (input: { id: string; facet: MaterialMenuLevels }) => void;
  removeMaterialHotfilter: (input: { id: string; facet: MaterialMenuLevels }) => void;
  removeAllMaterialHotfilters: () => void;
  menuState: MaterialMenuState;
  setMenuState: React.Dispatch<React.SetStateAction<MaterialMenuState>>;
  parentLevel: ParentMaterialMenuLevels | undefined;
  childLevel: MaterialMenuLevels | undefined;
  materialHotfilterCount: number;
}

const CatalogMaterialHotfilterContext = createContext<CatalogMaterialHotfilterContextProps>({} as never);

export const useCatalogMaterialHotfilterMenu = ({
  staticFilterGroups,
  filters,
  facets,
}: Pick<CatalogContextProps, 'staticFilterGroups' | 'filters' | 'facets'>) => {
  const { firstLevel, materialLvl0, materialLvl1 } = useMemo<Omit<MaterialMenuState, 'activeLevel'>>(() => {
    const getFacet = (facetType: MaterialMenuLevels, filterId: string) => {
      if (!filterId) {
        return null;
      }
      return facets?.[facetType]?.find((x) => String(x.id) === String(filterId));
    };

    const materialLvl0Filter = filters['materialLvl0.id']?.[0];
    const materialLvl0Facet = getFacet('materialLvl0', materialLvl0Filter);

    if (staticFilterGroups.materialLvl0) {
      return {
        firstLevel: 'materialLvl1',
        materialLvl0: { id: materialLvl0Filter, name: materialLvl0Facet?.name },
        materialLvl1: null,
      };
    }

    return {
      firstLevel: 'materialLvl0',
      materialLvl0: null,
      materialLvl1: null,
    };
  }, [filters, staticFilterGroups, facets]);

  return { firstLevel, materialLvl0, materialLvl1 };
};

const CatalogMaterialHotfilterProvider: React.FCWithChildren = ({ children }) => {
  const filters = useContextSelector(CatalogContext, (v) => v.filters);
  const setFilters = useContextSelector(CatalogContext, (v) => v.setFilters);
  const filterSources = useContextSelector(CatalogContext, (v) => v.filterSources);
  const setFilterSources = useContextSelector(CatalogContext, (v) => v.setFilterSources);
  const facets = useContextSelector(CatalogContext, (v) => v.facets);
  const staticFilterGroups = useContextSelector(CatalogContext, (v) => v.staticFilterGroups);
  const isManualSwitchRef = useContextSelector(CatalogContext, (v) => v.isManualSwitchRef);
  const { firstLevel, materialLvl0, materialLvl1 } = useCatalogMaterialHotfilterMenu({
    staticFilterGroups,
    facets,
    filters,
  });

  const { sendAnalyticEvent } = useAnalyticEvents('catalog');

  const [menuState, setMenuState] = useState<MaterialMenuState>({
    activeLevel: firstLevel,
    firstLevel,
    materialLvl0,
    materialLvl1,
  });

  const { parentLevel, childLevel } = useMemo(() => {
    return {
      parentLevel: getParentLevel(menuState.activeLevel),
      childLevel: getChildLevel(menuState.activeLevel),
    };
  }, [menuState.activeLevel]);

  const commit = (newFilters: ElasticSearch['filters'], additions: SourceAddition[] = []): FilterSources => {
    const newSources = syncFilterSources(newFilters, filterSources, additions);
    setFilters(newFilters);
    setFilterSources(newSources);
    return newSources;
  };

  const applyMaterialHotFilter = ({ id, facet }: { id: string; facet: MaterialMenuLevels }) => {
    isManualSwitchRef.current = true;
    // filter_details snapshots the user-applied filter state BEFORE this click (closure values),
    // so it represents the previous state; filters_applied carries the just-applied delta.
    const sendSnowplowEvent = (filters_applied: { [key: string]: string }) => {
      const payload = getApplyFilterPayload(filters, filters_applied, filterSources);
      sendAnalyticEvent('apply_filter', payload);
    };

    if (facet === 'materialLvl0') {
      // Allow multiple materialLvl0 to be selected at a time
      const currentMaterialLvl0Filters = filters['materialLvl0.id'] || [];
      const newMaterialLvl0Filters = new Set([...currentMaterialLvl0Filters, id]);

      const newState: ElasticSearch['filters'] = {
        ...filters,
        'materialLvl0.id': [...newMaterialLvl0Filters],
      };

      commit(newState, [{ key: 'materialLvl0.id', values: [id], source: FilterSource.User }]);
      sendSnowplowEvent({ 'materialLvl0.id': id });
      return;
    }

    if (facet === 'materialLvl1') {
      if (!menuState?.materialLvl0?.id) {
        return;
      }

      // we allow multiple materialLvl1 to be selected at a time
      const currentMaterialLvl0Filters = filters['materialLvl0.id'] || [];
      const currentMaterialLvl1Filters = filters['materialLvl1.id'] || [];

      // Add the parent materialLvl0 if not already in the filters
      const newMaterialLvl0Filters = new Set([...currentMaterialLvl0Filters, menuState.materialLvl0.id]);

      // Add the selected materialLvl1
      const newMaterialLvl1Filters = new Set([...currentMaterialLvl1Filters, id]);

      const newState: ElasticSearch['filters'] = {
        ...filters,
        'materialLvl0.id': [...newMaterialLvl0Filters],
        'materialLvl1.id': [...newMaterialLvl1Filters],
      };

      const additions: SourceAddition[] = [
        { key: 'materialLvl1.id', values: [id], source: FilterSource.User },
        { key: 'materialLvl0.id', values: [menuState.materialLvl0.id], source: FilterSource.User },
      ];
      commit(newState, additions);
      sendSnowplowEvent({ 'materialLvl0.id': menuState.materialLvl0.id, 'materialLvl1.id': id });
      return;
    }
    return;
  };

  const removeAllMaterialHotfilters = () => {
    if (staticFilterGroups.materialLvl1) {
      return;
    }
    isManualSwitchRef.current = true;
    if (staticFilterGroups.materialLvl0) {
      commit(removeKeys(filters, ['materialLvl1.id']));
      return;
    }
    commit(removeKeys(filters, ['materialLvl1.id', 'materialLvl0.id']));
  };

  const removeMaterialHotfilter = ({ id, facet }: { id: string; facet: MaterialMenuLevels }) => {
    isManualSwitchRef.current = true;
    if (facet === 'materialLvl0') {
      // Remove this materialLvl0 and all its materialLvl1 children
      const currentMaterialLvl0Filters = filters['materialLvl0.id'] || [];
      const currentMaterialLvl1Filters = filters['materialLvl1.id'] || [];

      // Remove the selected materialLvl0
      const newMaterialLvl0Filters = currentMaterialLvl0Filters.filter((filter) => filter !== id);

      // Find all materialLvl1 that belong to this materialLvl0 and remove them
      const childIdsToRemove =
        facets?.materialLvl1
          ?.filter((materialLvl1) => String(materialLvl1.parentID) === String(id))
          .map((materialLvl1) => materialLvl1.id) || [];

      const newMaterialLvl1Filters = currentMaterialLvl1Filters.filter((filter) => !childIdsToRemove.includes(filter));

      const newState: ElasticSearch['filters'] = {
        ...filters,
        ...(newMaterialLvl0Filters.length > 0 && { 'materialLvl0.id': newMaterialLvl0Filters }),
        ...(newMaterialLvl1Filters.length > 0 && { 'materialLvl1.id': newMaterialLvl1Filters }),
      };

      // Remove empty filter keys
      if (newMaterialLvl0Filters.length === 0) {
        delete newState['materialLvl0.id'];
      }
      if (newMaterialLvl1Filters.length === 0) {
        delete newState['materialLvl1.id'];
      }

      commit(newState);
      return;
    }
    if (facet === 'materialLvl1') {
      const currentMaterialLvl1Filters = filters['materialLvl1.id'] || [];
      const newMaterialLvl1Filters = currentMaterialLvl1Filters.filter((filter) => filter !== id);

      if (newMaterialLvl1Filters.length > 0) {
        commit({
          ...filters,
          'materialLvl1.id': newMaterialLvl1Filters,
        });
        return;
      }

      // If no materialLvl1 filters remain, just remove the materialLvl1 key
      const newState = { ...filters };
      delete newState['materialLvl1.id'];
      commit(newState);
    }
  };

  const materialHotfilterCount = useMemo(() => {
    const materialLvl1Count = filters['materialLvl1.id']?.length || 0;
    const materialLvl0Count = filters['materialLvl0.id']?.length || 0;
    return materialLvl1Count || materialLvl0Count;
  }, [filters]);

  return (
    <CatalogMaterialHotfilterContext.Provider
      value={{
        applyMaterialHotFilter,
        removeMaterialHotfilter,
        removeAllMaterialHotfilters,
        menuState,
        setMenuState,
        parentLevel,
        childLevel,
        materialHotfilterCount,
      }}
    >
      {children}
    </CatalogMaterialHotfilterContext.Provider>
  );
};

export { CatalogMaterialHotfilterProvider, CatalogMaterialHotfilterContext };
